import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../atoms/LoadingSpinner';
import BandPage from '../BandPage';
import bandService from '../../../services/bandService';
import { eventService } from '../../../services/eventService';
import useBandPosts from '../../../hooks/useBandPosts';
import { buildBandShareUrl, normalizeBandRouteHandle } from '../../../utils/bandRoutes';
import { toPublicBandImageUrl } from '../../../utils/mediaUrl';
import { BAND_UPCOMING_EVENT_TARGET_COUNT } from '../BandsWelcomePage/constants';
import useBandMediaLibrary from '../BandsWelcomePage/hooks/useBandMediaLibrary';
import { toBandPageBand } from '../BandsWelcomePage/utils/bandWelcomeTransforms';
import {
  isUpcomingBandEvent,
  resolveBandAvatarImageUrl,
  resolveBandCoverImageUrl,
} from '../BandPage/adaptBand';
import '../PublicProfilePage/index.scss';

const normalizePublicBandRouteHandle = (value) => {
  if (typeof value !== 'string' || !value.startsWith('@')) {
    return null;
  }
  return normalizeBandRouteHandle(value.slice(1));
};

export default function PublicBandPage() {
  const { bandHandle } = useParams();
  const normalizedHandle = normalizePublicBandRouteHandle(bandHandle);
  const [band, setBand] = useState(null);
  const [state, setState] = useState('loading');
  const mediaRequestScopeRef = useRef(0);
  const publicAvatarImageUrl = useMemo(() => (
    toPublicBandImageUrl(resolveBandAvatarImageUrl(band))
  ), [band]);
  const publicCoverImageUrl = useMemo(() => (
    toPublicBandImageUrl(resolveBandCoverImageUrl(band))
  ), [band]);
  const publicBand = useMemo(() => {
    if (!band || (!publicAvatarImageUrl && !publicCoverImageUrl)) {
      return band;
    }
    const images = { ...(band.images || {}) };
    if (publicAvatarImageUrl) {
      images.avatar = {
        ...(typeof images.avatar === 'object' && images.avatar !== null ? images.avatar : {}),
        url: publicAvatarImageUrl,
      };
    }
    if (publicCoverImageUrl) {
      images.cover = {
        ...(typeof images.cover === 'object' && images.cover !== null ? images.cover : {}),
        url: publicCoverImageUrl,
      };
    }
    return {
      ...band,
      avatarUrl: publicAvatarImageUrl || band.avatarUrl,
      coverImageUrl: publicCoverImageUrl || band.coverImageUrl,
      images,
    };
  }, [band, publicAvatarImageUrl, publicCoverImageUrl]);
  const bandPosts = useBandPosts(band, null, {
    enabled: state === 'ready' && Boolean(band?.id),
    audience: 'public',
  });
  const bandMedia = useBandMediaLibrary({
    selectedBand: publicBand,
    currentUserId: null,
    canEditSelectedBand: false,
    audience: 'public',
    previewRequestIdRef: mediaRequestScopeRef,
  });

  useEffect(() => {
    let cancelled = false;
    setBand(null);
    if (!normalizedHandle) {
      setState('not-found');
      return undefined;
    }
    setState('loading');
    bandService.getPublicBandByHandle(normalizedHandle)
      .then(async (details) => {
        if (cancelled) return;
        const pageBand = toBandPageBand(details, {
          id: details?.id,
          handle: normalizedHandle,
          name: normalizedHandle,
        });
        setBand(pageBand);
        setState('ready');
        if (!pageBand?.id) return;
        try {
          const events = await eventService.getPublicBandEvents(pageBand.id, {
            limit: BAND_UPCOMING_EVENT_TARGET_COUNT,
            offset: 0,
            upcomingOnly: true,
          });
          if (!cancelled) {
            setBand((current) => (current?.id === pageBand.id
              ? {
                ...current,
                events: Array.isArray(events)
                  ? events.filter(isUpcomingBandEvent).slice(0, BAND_UPCOMING_EVENT_TARGET_COUNT)
                  : [],
              }
              : current));
          }
        } catch (_) {
          if (!cancelled) {
            setBand((current) => (current?.id === pageBand.id ? { ...current, events: [] } : current));
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState('not-found');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedHandle]);

  const shareUrl = useMemo(() => (band ? buildBandShareUrl(band) : null), [band]);
  const handleShare = () => {
    if (!shareUrl || typeof navigator === 'undefined') return;
    if (typeof navigator.share === 'function') {
      navigator.share({ url: shareUrl, title: band?.name }).catch(() => {});
      return;
    }
    navigator.clipboard?.writeText?.(shareUrl);
  };

  if (state === 'loading') {
    return (
      <main className="public-page-status" aria-busy="true">
        <LoadingSpinner size={40} />
      </main>
    );
  }

  if (state !== 'ready' || !band) {
    return (
      <main className="public-page-status">
        <h1>Band not found</h1>
      </main>
    );
  }

  return (
    <BandPage
      band={publicBand}
      canEdit={false}
      className="band-page--public"
      currentUserId={null}
      avatarImageUrl={publicAvatarImageUrl}
      coverImageUrl={publicCoverImageUrl}
      onShare={shareUrl ? handleShare : undefined}
      posts={bandPosts.posts}
      tracks={bandMedia.tracks}
      playsCount={bandMedia.playsCount}
      galleryPhotos={bandMedia.galleryPhotos}
      playingTrackId={bandMedia.playingTrackId}
      trackProgressById={bandMedia.trackProgressById}
      onPlayTrack={bandMedia.handlePlayTrack}
      onSeekTrack={bandMedia.handleSeekTrack}
    />
  );
}
